import { create } from 'zustand';

export enum AIJobNotificationType {
  AUTHORIZATION_SUMMARY = 'authorizationSummary',
  AUTHORIZATION_COMPARISON = 'authorizationComparison',
  REFERRAL_SUMMARY = 'referralSummary',
  WORKSPACE_SUMMARY = 'workspaceSummary',
}

export type AIJobNotificationContext = {
  jobId: string;
  jobType: AIJobNotificationType;
  summaryType?: string;
  actionSuccessText?: string;
  actionFailureText?: string;
  jobbableId: string;
  patientName?: string;
  returnUrl: string; // also the URL we check to see if we need to hide the notification
  createdAt: Date;
};

type AIJobNotificationStore = {
  // Active job tracking
  trackedJobs: Map<string, AIJobNotificationContext>;

  // Dismissed jobs that are still processing
  // When these jobs complete/error, we re-show the notification
  dismissedProcessingJobs: Set<string>;

  // Notification ID mapping (jobId -> notificationId)
  jobNotificationMap: Map<string, string>;

  // Actions
  trackAIJob: (context: AIJobNotificationContext) => void;
  untrackAIJob: (jobId: string) => void;
  updateTrackedAIJob: (
    jobId: string,
    newContext: Partial<Omit<AIJobNotificationContext, 'jobId' | 'jobType' | 'jobbableId'>>
  ) => void;
  dismissJobNotification: (jobId: string, isProcessing: boolean) => void;
  addJobNotificationMapping: (jobId: string, notificationId: string) => void;
  removeJobNotificationMapping: (jobId: string) => void;
  clearDismissedProcessingJob: (jobId: string) => void;
};

export const useAIJobNotificationStore = create<AIJobNotificationStore>((set) => ({
  trackedJobs: new Map(),
  dismissedProcessingJobs: new Set(),
  jobNotificationMap: new Map(),

  trackAIJob: (context) => {
    set((state) => {
      if (state.trackedJobs.has(context.jobId)) return state;

      const newTrackedJobs = new Map(state.trackedJobs);
      newTrackedJobs.set(context.jobId, context);
      return { trackedJobs: newTrackedJobs };
    });
  },

  untrackAIJob: (jobId: string) => {
    set((state) => {
      const newTrackedJobs = new Map(state.trackedJobs);
      const newJobNotificationMap = new Map(state.jobNotificationMap);
      const newDismissedProcessingJobs = new Set(state.dismissedProcessingJobs);

      newTrackedJobs.delete(jobId);
      newJobNotificationMap.delete(jobId);
      newDismissedProcessingJobs.delete(jobId);

      return {
        trackedJobs: newTrackedJobs,
        jobNotificationMap: newJobNotificationMap,
        dismissedProcessingJobs: newDismissedProcessingJobs,
      };
    });
  },

  updateTrackedAIJob(jobId, newContext) {
    set((state) => {
      const existingJob = state.trackedJobs.get(jobId);
      if (!existingJob) return state;

      const newTrackedJobs = new Map(state.trackedJobs);
      newTrackedJobs.set(jobId, { ...existingJob, ...newContext });
      return { trackedJobs: newTrackedJobs };
    });
  },

  dismissJobNotification: (jobId, isProcessing) => {
    set((state) => {
      const newDismissedProcessingJobs = new Set(state.dismissedProcessingJobs);

      if (isProcessing) {
        newDismissedProcessingJobs.add(jobId);
      } else {
        // Job already complete/errored, just clean up
        const newTrackedJobs = new Map(state.trackedJobs);
        const newJobNotificationMap = new Map(state.jobNotificationMap);
        newTrackedJobs.delete(jobId);
        newJobNotificationMap.delete(jobId);

        return {
          trackedJobs: newTrackedJobs,
          jobNotificationMap: newJobNotificationMap,
        };
      }

      return { dismissedProcessingJobs: newDismissedProcessingJobs };
    });
  },

  addJobNotificationMapping: (jobId, notificationId) => {
    set((state) => {
      const newJobNotificationMap = new Map(state.jobNotificationMap);
      newJobNotificationMap.set(jobId, notificationId);
      return { jobNotificationMap: newJobNotificationMap };
    });
  },

  removeJobNotificationMapping: (jobId) => {
    set((state) => {
      const newJobNotificationMap = new Map(state.jobNotificationMap);
      newJobNotificationMap.delete(jobId);
      return { jobNotificationMap: newJobNotificationMap };
    });
  },

  clearDismissedProcessingJob: (jobId) => {
    set((state) => {
      const newDismissedProcessingJobs = new Set(state.dismissedProcessingJobs);
      newDismissedProcessingJobs.delete(jobId);
      return { dismissedProcessingJobs: newDismissedProcessingJobs };
    });
  },
}));
